import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PreregisterPlayNowButton from './PreregisterPlayNowButton';
import GameUsrEvntApi from 'Apis/GameUsrEvntApi';
import type { GameUsrEvntApiBody } from 'Apis/GameUsrEvntApi';
import { fetchAdPayload, getKstTimestamp, useGtm } from 'hooks/useGtm';

type PreregisterNewsletterSectionProps = {
    viewState: 'preLogin' | 'success';
    onLoginClick: () => void;
};

type IdntCdGroup = 'mobileVerified' | 'notVerified' | 'unknown';

function classifyIdntCd(idntCd: string | null | undefined): IdntCdGroup {
    const normalized = String(idntCd || '').trim().toUpperCase();
    if (!normalized) return 'unknown';

    // Project uses only these identification method codes.
    if (normalized === 'MBP') return 'mobileVerified';
    if (normalized === 'NA' || normalized === 'EML') return 'notVerified';

    return 'notVerified';
}
function PreLoginView({ onLoginClick }: { onLoginClick: () => void }) {
    return (
        <>
            <h3>사전예약 하기</h3>
            <div className="preregisterContainerTxt">
                <p>
                지금 바로 사전예약하고 <br />
                특별 보상을 챙겨가세요 !
                </p>
                <p className='dateTxt'>기간 : 2026.07.29 ~ 정식 오픈 전</p>
            </div>
            <div className="preregisterContainerBtn">
                <Link to="/login" className="preregisterBtn" onClick={onLoginClick}>
                    <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/loginBtn.png'}`} alt="사전예약 하기" />
                </Link>
            </div>
        </>
    );
}

function SuccessView({ idntCdGroup }: { idntCdGroup: IdntCdGroup }) {
    const isMobileVerified = idntCdGroup === 'mobileVerified';
    const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;

    return (
        <>

            <h3>사전예약 완료!</h3>
            <div className="preregisterContainerTxt">
                <p className='dateTxt'>사전예약이 정상적으로 완료되었습니다.</p>
            </div>
            {isMobileViewport ? (
                <>
                <div className="preregisterRewardBtnDl success">
                    {isMobileVerified ? 
                        <>
                            <h3>
                                정식 오픈 후 <span>알림을 확인</span> 을 확인하세요
                            </h3>
                        </> : <h3>정식 오픈 알림 받기</h3>}
                    {isMobileVerified ? 
                        <>
                            <ul>
                                <li>
                                    <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/verified.png'}`} alt="인증 완료" />
                                </li>
                            </ul>
                        </>:
                        <>
                            <ul>
                                <li>
                                    <Link to="/MyPage/idntMthdChng">
                                        <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/mbpAthnBtn.png'}`} alt="휴대폰 인증 " />
                                    </Link>
                                </li>
                            </ul>
                        </>
                    }
                </div>
                </>
            ) : (
                <div className="preregisterContainerBtn">
                    <PreregisterPlayNowButton />
                </div>
            )}
        </>
    );
}

function PreLoginRewardSection() {
    return (
        <div className="preregisterRewardWrap pre">
            <div className="preregisterRewardTitl">
                <h2><span className="grandopenCol">사전예약</span> 한정 특별 보상</h2>
                <h3>베어브릭의 첫 여정을 위한 사전예약 보상!</h3>
            </div>
            <div className="preregisterRewardLst">
                <img src={`https://bb-cdn.gameking.com/web/event/grandopen/logoutRewardImg.png`} alt="로그인 전 사전예약 보상 아이템" />
            </div>
            <div className="preregisterRewardBtn">
                <div className="preregisterRewardBtnDl">
                    <h3>다운로드</h3>
                    <ul>
                        <li>
                            <Link to="/gamepage/download?gameSvcCd=BBO">
                                <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/WindowIcon.png'}`} alt="Windows 다운로드" />
                            </Link>
                        </li>
                        <li>
                            <Link to="/gamepage/download?gameSvcCd=BBO">
                                <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/MacIcon.png'}`} alt="Mac 다운로드" />
                            </Link>
                        </li>
                    </ul>
                </div>
                <div className="preregisterRewardBtnDl">
                    <h3>공식 커뮤니티</h3>
                    <ul>
                        <li>
                            <a href="https://cafe.naver.com/bearbrickheroes" target="_blank" rel="noopener noreferrer">
                                <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/NVRIcon.png'}`} alt="Naver 카페 이동" />
                            </a>
                        </li>
                        <li>
                            <a href="https://discord.gg/H6aeYKyAa3" target="_blank" rel="noopener noreferrer">
                                <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/DSCDIcon.png'}`} alt="Discord 이동" />
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

function SuccessRewardSection({ idntCdGroup, usrEvntDataState, onRefreshUsrEvntData }: { idntCdGroup: IdntCdGroup, usrEvntDataState: GameUsrEvntApiBody | null, onRefreshUsrEvntData: () => void }) {
    const isMobileVerified = idntCdGroup === 'mobileVerified' || usrEvntDataState?.prog3Yn === 'Y';
    const [isRefreshCooldown, setIsRefreshCooldown] = React.useState(false);
    const refreshCooldownTimerRef = React.useRef<number | null>(null);
    const isProg1Done = usrEvntDataState?.prog1Yn === 'Y';
    const isProg2Done = usrEvntDataState?.prog2Yn === 'Y';
    const isProg3Done = usrEvntDataState?.prog3Yn === 'Y';
    const progressStatusItems = [
        { key: '1',title:'캐릭터 생성', label: '성장 지원 패키지', value: isProg1Done ? '완료' : '진행중' },
        { key: '2',title:'10레벨 달성', label: 'Premium 스타터 패키지', value: isProg2Done ? '완료' : '진행중' },
        { key: '3',title:'30레벨 달성', label: 'Ultimate 스타터 패키지', value: isProg3Done ? '완료' : '미완료' },
    ];

    useEffect(() => {
        return () => {
            if (refreshCooldownTimerRef.current !== null) {
                window.clearTimeout(refreshCooldownTimerRef.current);
            }
        };
    }, []);

    const handleRefreshClick = () => {
        if (isRefreshCooldown) return;

        onRefreshUsrEvntData();
        setIsRefreshCooldown(true);

        refreshCooldownTimerRef.current = window.setTimeout(() => {
            setIsRefreshCooldown(false);
            refreshCooldownTimerRef.current = null;
        }, 10000);
    };

    return (
        <div className="preregisterRewardWrap success">
            <div className="preregisterRewardTitl">
                <h2><span className="grandopenCol">지금 플레이</span>만 해도 오픈 후 특별 보상이?!</h2>
                <h3>이벤트 기간 : 2026.07.29 ~ 2026.08.23</h3>
            </div>
            <div className="preregisterRewardLst login">
                <ul>
                    {progressStatusItems.map((item) => (
                        <li key={item.key} className={item.value === '완료' ? 'completed' : 'in-progress'}>
                            <h2>{item.title}</h2>
                            <img src={`${process.env.PUBLIC_URL + `https://bb-cdn.gameking.com/web/event/grandopen/reward${item.key}.png`}`} alt={item.value} />
                            <p>{item.label}</p>
                        </li>
                    ))}
                </ul>
                <button onClick={handleRefreshClick} className="refreshBtn" title={isRefreshCooldown ? "10초 후 다시 시도" : "새로고침"} disabled={isRefreshCooldown}>
                    <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/refreshBtn.png'}`} alt="새로고침" />
                </button>
            </div>
            <div className="preregisterRewardBtn">
                
                <div className="preregisterRewardBtnDl success">
                    {isMobileVerified ? 
                    <>
                    <h3>
                        정식 오픈 후 <span>알림을</span> 확인 하세요
                    </h3>
                    </> : <h3>정식 오픈 알림 받기</h3>}
                    {isMobileVerified ? 
                    <>
                        <ul>
                            <li>
                                <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/verified.png'}`} alt="인증 완료" />
                            </li>
                        </ul>
                    </>:
                    <>
                        <ul>
                            <li>
                                <Link to="/MyPage/idntMthdChng">
                                    <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/mbpAthnBtn.png'}`} alt="휴대폰 인증 " />
                                </Link>
                            </li>
                        </ul>
                    </>
                    }
                </div>
            </div>
        </div>
    );
}

const PreregisterNewsletterSection = ({
    viewState,
    onLoginClick,
}: PreregisterNewsletterSectionProps) => {
    const loginInfoState = useSelector((state: any) => state.LoginInfoState?.LoginInfo?.apiBody);
    const idntCdGroup = classifyIdntCd(loginInfoState?.idntCd ?? loginInfoState?.idntMthdCd);
    const [usrEvntDataState, setUsrEvntDataState] = React.useState<GameUsrEvntApiBody | null>(null);
    const { push: gtmPush } = useGtm();
    useEffect(() => {
        if(viewState === 'success') {
            (async () => {
                const resp = await GameUsrEvntApi("BBO");
                if(resp?.apiHdr?.code === 'GAME0000') {
                    setUsrEvntDataState(resp.apiBody);
                }
            })();
        }
    },[viewState]);
    const reFreshUsrEvntData = () => {
        GameUsrEvntApi("BBO").then((resp) => {
            if(resp?.apiHdr?.code === 'GAME0000') {
                setUsrEvntDataState(resp.apiBody);
            }
        });
    };
    const renderStateView = () => {
        switch (viewState) {
            case 'preLogin':
                return <PreLoginView onLoginClick={onLoginClick} />;
            case 'success':
                return <SuccessView idntCdGroup={idntCdGroup} />;
            default:
                return <PreLoginView onLoginClick={onLoginClick} />;
        }
    };
    

    return (
        <div className="newsletter-section" id="formSec">
            <div className="preregisterContainerwrap">
                <div className={`preregisterContainer form-section ${viewState === 'success' ? 'success' : ''}`}>
                    <div className="preregisterContainerTitlImgWrap">
                        <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/bboLogo.png'}`} alt="베어브릭 히어로즈 로고" />
                        <img src={`${process.env.PUBLIC_URL + '/images/evnt/grandopen/GrandOpenTitl.png'}`} alt="사전등록 이미지" />
                    </div>
                    {renderStateView()}
                </div>
            </div>
            {viewState === 'success' ? <SuccessRewardSection idntCdGroup={idntCdGroup} usrEvntDataState={usrEvntDataState} onRefreshUsrEvntData={reFreshUsrEvntData} /> : <PreLoginRewardSection />}
        </div>
    );
};

export default PreregisterNewsletterSection;
